import {
  JobAppointmentScheduledEventDetails,
  RecordAppointmentScheduledRequest,
  RecordAppointmentScheduledRequestBody,
  RecordAppointmentScheduledResponse,
} from '@eigtech/restoration-types'
import { GetAppointmentsResponse } from '@eigtech/summarizer-types'
import { contextualPost, useMutation, useQueryClient } from '@eigtech/ui-shared-api'
import { EntityAdminJob } from '../types/EntityAdminJob'
import { AppointmentsQueryKey, jobsQueryKeys, restorationBasePath } from './constants'
import { useOptimisticAddJobTimelineEvent } from './useOptimisticAddJobTimelineEvent'

const recordAppointmentScheduled = ({ jobId, ...payload }: RecordAppointmentScheduledRequest) =>
  contextualPost<RecordAppointmentScheduledResponse, RecordAppointmentScheduledRequestBody>(
    `${restorationBasePath}/job/${jobId}/record-appointment-scheduled`,
    payload
  )

export function useRecordAppointmentScheduled(appointmentsQueryKey?: AppointmentsQueryKey) {
  const queryClient = useQueryClient()
  const optimisticAddJobTimelineEvent = useOptimisticAddJobTimelineEvent()

  return useMutation({
    mutationFn: recordAppointmentScheduled,
    async onSuccess(appointment, { jobId }) {
      const queryKey = jobsQueryKeys.detail(jobId)

      await queryClient.cancelQueries({ queryKey })

      const previousJob = queryClient.getQueryData<EntityAdminJob>(queryKey)

      if (previousJob) {
        queryClient.setQueryData<EntityAdminJob>(queryKey, () => ({
          ...previousJob,
          appointments: [
            ...(previousJob?.appointments ?? []),
            {
              ...appointment,
              isCompleted: false,
            },
          ],
        }))
      }

      if (appointmentsQueryKey !== undefined) {
        await queryClient.cancelQueries({ queryKey: appointmentsQueryKey })

        const previousAppointments =
          queryClient.getQueryData<GetAppointmentsResponse['appointments']>(appointmentsQueryKey)

        if (previousAppointments) {
          queryClient.setQueryData<GetAppointmentsResponse['appointments']>(
            appointmentsQueryKey,
            () => [
              ...previousAppointments,
              {
                ...appointment,
                isCompleted: false,
              },
            ]
          )
        }
      }

      await optimisticAddJobTimelineEvent<JobAppointmentScheduledEventDetails>({
        jobId,
        timelineEvent: 'appointmentScheduled',
        point: {
          date: appointment.appointmentDate,
          details: appointment,
        },
      })
    },
  })
}
